import * as pc from 'playcanvas';
import type { PathPose } from './pathPoseFollower';
import {
    getSegmentIndices,
    sampleWaypointPathSegment,
    type WaypointInterpolation,
    type WaypointPathNode,
} from './waypointPath';

export type CameraPathLoopMode = 'loop' | 'pingpong' | 'none';

/**
 * How path progress is spread over the segments.
 * - 'uniform': every segment gets the same share of the timeline — how the scene tour
 *   paces its waypoints.
 * - 'distance': each segment gets a share proportional to how far the camera travels
 *   (turns count too), so unevenly spaced waypoints still play at a steady pace.
 */
export type CameraPathTiming = 'uniform' | 'distance';

export interface CameraPathSamplerOptions {
    /** Default 'none'. 'loop' closes the path back to the first pose. */
    loopMode?: CameraPathLoopMode;
    /** Default 'smooth' (Catmull-Rom through the poses, like the scene tour). */
    interpolation?: WaypointInterpolation;
    /** Default 'uniform'. */
    timing?: CameraPathTiming;
}

export interface CameraPathSampler {
    readonly poseCount: number;
    readonly segmentCount: number;
    /**
     * Pose at timeline progress [0, 1], in the same space as the input poses. For
     * 'pingpong' the timeline runs out to the last pose and back. Null when the path
     * has fewer than 2 poses.
     */
    sample(progress: number): PathPose | null;
}

/** Tour-timeline progress → position along the path (0 = first pose, 1 = end). */
export function timelineToPathProgress(progress: number, loopMode: CameraPathLoopMode): number {
    if (loopMode === 'loop') return ((progress % 1) + 1) % 1;
    if (loopMode === 'pingpong') {
        // Progress 1 wraps to cycle 0: the end of a full out-and-back is the first pose.
        const cycle = ((progress % 1) + 1) % 1;
        return cycle <= 0.5 ? cycle * 2 : (1 - cycle) * 2;
    }
    return Math.max(0, Math.min(1, progress));
}

/** Angle (radians) between two unit quaternions. */
function quatAngle(a: pc.Quat, b: pc.Quat): number {
    const dot = Math.min(1, Math.abs(a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w));
    return 2 * Math.acos(dot);
}

const LENGTH_SUBDIVISIONS = 16;

/**
 * Build a deterministic sampler for a camera path through arbitrary poses (for example
 * waypoints a visitor drops while exploring). Positions follow the same curve the scene
 * tour uses; rotations slerp and FOV lerps between the poses at each segment's ends.
 */
export function createCameraPathSampler(poses: PathPose[], options: CameraPathSamplerOptions = {}): CameraPathSampler {
    const loopMode: CameraPathLoopMode = options.loopMode ?? 'none';
    const interpolation: WaypointInterpolation = options.interpolation ?? 'smooth';
    const nodes: WaypointPathNode[] = poses.map((pose) => ({
        position: { x: pose.position.x, y: pose.position.y, z: pose.position.z },
    }));
    const rotations = poses.map((pose) => {
        const q = new pc.Quat(pose.rotation.x, pose.rotation.y, pose.rotation.z, pose.rotation.w);
        return q.normalize();
    });
    const fovs = poses.map((pose) => pose.fov);
    const segmentCount = poses.length < 2 ? 0 : (loopMode === 'loop' ? poses.length : poses.length - 1);

    // Cumulative share of the timeline at the start of each segment (length segmentCount + 1).
    const bounds: number[] = [0];
    if (segmentCount > 0) {
        let weights = new Array<number>(segmentCount).fill(1);
        if (options.timing === 'distance') {
            const lengths: number[] = [];
            const angles: number[] = [];
            for (let s = 0; s < segmentCount; s++) {
                let length = 0;
                let prev = sampleWaypointPathSegment(nodes, s, 0, loopMode, interpolation);
                for (let i = 1; i <= LENGTH_SUBDIVISIONS; i++) {
                    const next = sampleWaypointPathSegment(nodes, s, i / LENGTH_SUBDIVISIONS, loopMode, interpolation);
                    if (prev && next) {
                        length += Math.hypot(next.x - prev.x, next.y - prev.y, next.z - prev.z);
                    }
                    prev = next;
                }
                const indices = getSegmentIndices(nodes, s, loopMode);
                lengths.push(length);
                angles.push(indices ? quatAngle(rotations[indices.startIndex], rotations[indices.endIndex]) : 0);
            }
            const totalLength = lengths.reduce((sum, length) => sum + length, 0);
            // A quarter turn is worth one average segment of travel, so pure turns still take time.
            const turnScale = totalLength > 0 ? (totalLength / segmentCount) / (Math.PI / 2) : 1;
            const raw = lengths.map((length, s) => length + angles[s] * turnScale);
            const total = raw.reduce((sum, w) => sum + w, 0);
            if (total > 0) {
                // Floor each share so a zero-length, zero-turn segment is still passed through.
                const floor = total / segmentCount * 0.05;
                weights = raw.map((w) => Math.max(w, floor));
            }
        }
        const totalWeight = weights.reduce((sum, w) => sum + w, 0);
        let acc = 0;
        for (let s = 0; s < segmentCount; s++) {
            acc += weights[s];
            bounds.push(acc / totalWeight);
        }
        bounds[segmentCount] = 1;
    }

    const scratch = new pc.Quat();

    return {
        poseCount: poses.length,
        segmentCount,
        sample(progress: number): PathPose | null {
            if (segmentCount === 0) return null;
            const p = timelineToPathProgress(progress, loopMode);
            let segmentIndex = segmentCount - 1;
            for (let s = 0; s < segmentCount; s++) {
                if (p < bounds[s + 1]) {
                    segmentIndex = s;
                    break;
                }
            }
            const span = bounds[segmentIndex + 1] - bounds[segmentIndex];
            const t = span > 0 ? Math.min(1, Math.max(0, (p - bounds[segmentIndex]) / span)) : 0;
            const indices = getSegmentIndices(nodes, segmentIndex, loopMode);
            const position = sampleWaypointPathSegment(nodes, segmentIndex, t, loopMode, interpolation);
            if (!indices || !position) return null;
            scratch.slerp(rotations[indices.startIndex], rotations[indices.endIndex], t);
            const startFov = fovs[indices.startIndex];
            const endFov = fovs[indices.endIndex];
            return {
                position: { x: position.x, y: position.y, z: position.z },
                rotation: { x: scratch.x, y: scratch.y, z: scratch.z, w: scratch.w },
                fov: startFov + (endFov - startFov) * t,
            };
        },
    };
}
